import { useEffect, useRef } from 'react'
import { fetchWithAuth, getAuth, onAuthChange, setAuth, bitolApiBase } from '../lib/auth.js'

// After auth.accessToken becomes truthy, fetch GET /v2/users/profile once
// per session and merge { firstName, lastName, isAdmin } into auth state.
//
// The Bitol-AS-issued JWT carries `email` and `realm_access.roles` but NOT
// the user's name — historically Workbench's legacy SPA password POST set
// firstName/lastName directly. OAuth-only flows don't, so this hook backfills
// from the canonical user profile endpoint.
//
// useMe() separately fetches /api/v1/me for the header avatar / display name;
// this hook is the contract-shape backfill for code that reads auth.firstName
// (e.g. future personalization, analytics tags).
export default function useProfileHydration() {
  const doneEmailRef = useRef(null)

  useEffect(() => {
    const hydrate = async () => {
      const auth = getAuth()
      if (!auth.accessToken || !auth.email) return
      if (doneEmailRef.current === auth.email) return
      doneEmailRef.current = auth.email
      try {
        const res = await fetchWithAuth(`${bitolApiBase()}/v2/users/profile`, {
          headers: { Accept: 'application/json' },
        })
        if (!res.ok) return
        const profile = await res.json().catch(() => null)
        if (!profile) return
        setAuth({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          isAdmin: typeof profile.isAdmin === 'boolean' ? profile.isAdmin : getAuth().isAdmin,
        })
      } catch (err) {
        console.warn('[useProfileHydration] /v2/users/profile failed:', err?.message)
      }
    }
    hydrate()
    const off = onAuthChange(hydrate)
    return () => off()
  }, [])
}
